import React, { useState, useEffect, useRef } from 'react';
import ErpiaSelect from './ErpiaSelect';
import { confirmErpia } from './ConfirmDialog';
import { Plus, Eye, Trash2, Upload, FileText, X, Paperclip, Pencil, RefreshCw, Download, FileType } from 'lucide-react';
import APIService from '../services/api';
import CanonicalDocumentViewer from './DocumentViewer';
import { estCvDocument, estCvProduit } from '../utils/cvDocument';
import { avecCle } from '../services/cleLecture';

const BASE = 'https://besoins-tat-production.up.railway.app';

/** Document bureautique : seul cas où proposer la conversion Word/PDF a un sens. */
// LA BARRE D'ICÔNES D'UNE RANGÉE, définie UNE fois. Six copies de la même
// chaîne de classes, c'était six endroits où la retoucher — et la raison pour
// laquelle le CV avait fini avec une barre différente des autres documents.
const BTN_ICONE = 'border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-800 p-1.5 rounded-lg transition-colors';
const BTN_ICONE_DANGER = 'border border-gray-200 bg-white text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition-colors';

const pieceOffice = (piece) => {
  const noms = `${piece?.originalName || ''} ${piece?.label || ''} ${piece?.url || ''}`;
  if (/\.(pdf|jpe?g|png|gif|webp)(\?|$|\s)/i.test(noms)) return false;
  if (/\.(docx?|xlsx?|pptx?)(\?|$|\s)/i.test(noms)) return true;
  if (/word|officedocument|msword/i.test(piece?.mime_type || '')) return true;
  return piece?.generatedPrep === true;
};

/**
 * Bouton Télécharger de la ligne : le survol propose Word (fichier natif) et
 * PDF (converti par le serveur). Les documents de positionnement passent par
 * leur propre route — même contrat.
 */
const PieceDownload = ({ piece, candId }) => {
  const base = avecCle(piece.positionDoc
    ? `${BASE}/position-documents/${piece.id}/preview?download=1`
    : `${BASE}/candidates/${candId}/documents/${piece.id}/preview?download=1`);
  const cls = `${BTN_ICONE} inline-flex`;
  if (!pieceOffice(piece)) {
    return <a href={base} download title="Télécharger" className={cls}><Download className="w-3.5 h-3.5" /></a>;
  }
  // Premier choix = format NATIF (octets tels quels), nommé d'après
  // l'extension : une matrice .pptx propose « PowerPoint », pas « Word ».
  const noms = `${piece?.originalName || ''} ${piece?.label || ''}`;
  const lblNatif = /\.pptx?(\?|$|\s)/i.test(noms) ? 'PowerPoint (.pptx)'
    : /\.xlsx?(\?|$|\s)/i.test(noms) ? 'Excel (.xlsx)' : 'Word (.docx)';
  return (
    <span className="ats-dl-split">
      <a href={base} download title="Télécharger — survolez pour choisir le format" className={cls}><Download className="w-3.5 h-3.5" /></a>
      <span className="ats-dl-menu">
        <a href={base} download><FileType className="w-3.5 h-3.5" /> {lblNatif}</a>
        <a href={`${base}&format=pdf`} download><FileText className="w-3.5 h-3.5" /> PDF</a>
      </span>
    </span>
  );
};

// Types de pièce reconnus par le dossier AO — le type choisi classe la pièce
// directement, plus de détection automatique par le nom de fichier.
const TYPE_PIECE_OPTIONS = [
  { value: 'document', label: 'Document' },
  { value: 'cv_tat', label: 'CV format entreprise' },
  { value: 'matrice', label: 'Matrice de compétences' },
  { value: 'cni', label: 'CNI' },
  { value: 'permis', label: 'Permis' },
];

// UN SEUL SYSTÈME DE RANGÉE. La carte besoin affichait DEUX barres d'actions
// côte à côte : le CV avait des boutons-texte (« Voir · Télécharger ·
// Supprimer », fabriqués dans App.js à partir des champs legacy de la fiche)
// tandis que les autres documents avaient les boutons-icônes (œil, crayon,
// téléchargement, corbeille). Vécu 11.09 : « pourquoi je n'ai pas le même
// menu avec l'œil partout ». Deux barres, c'est aussi deux endroits où
// corriger la même chose — et elles avaient déjà divergé (le CV n'avait pas
// « Renommer », la matrice n'avait pas le choix Word/PDF).
//
// Le CV passe donc par `rangeePiece`, comme tout le reste. Il reste une seule
// exception, et elle n'est PAS visuelle : une fiche dont le CV n'existe qu'en
// legacy (`cv_url`, jamais miroité en candidate_documents) n'a pas de pièce
// canonique à afficher. Elle produit alors une rangée bâtie sur ces
// champs-là — même rendu, mêmes icônes — plutôt que de faire disparaître le
// CV de l'écran.
const PiecesJointesManager = ({ candidat, besoinId, showNotification, refreshKey,
  cvFiche = null, onSupprimerCvFiche, suppressionCvEnCours = false, onUploadCvFiche }) => {
  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [newPiece, setNewPiece] = useState({ label: '', file: null, typePiece: 'document' });
  const [dropDocs, setDropDocs] = useState(false); // glisser-déposer sur le bloc Documents
  const [viewingPiece, setViewingPiece] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null); // { piece, value } → modale de renommage
  const [renaming, setRenaming] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // piece → modale de confirmation
  const [deleting, setDeleting] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState(null); // pièce de positionnement à remplacer
  const [replacingId, setReplacingId] = useState(null);
  // Doublons que le SERVEUR a déjà écartés de la liste (?fiche=1) : on le dit,
  // sinon un fichier déposé deux fois « disparaît » sans explication.
  const [doublonsMasques, setDoublonsMasques] = useState(0);
  // Pièces que le serveur n'a pas servies ici parce qu'elles appartiennent à
  // un AUTRE besoin du candidat (son CV format entreprise préparé ailleurs).
  // Ce n'est pas un doublon : on ne le dit pas avec les mêmes mots.
  const [autresBesoins, setAutresBesoins] = useState(0);
  // Les fichiers CV qui ne sont pas celui du besoin : rangés, pas effacés.
  const [voirAutresCv, setVoirAutresCv] = useState(false);
  const replaceFileRef = useRef(null);

  // API canonique candidate_documents — le backend résout id canonique OU legacy,
  // on privilégie le canonique. Les pièces uploadées ici entrent dans les dossiers AO
  // (rapport de complétude + ZIP) au même titre que les anciennes /pieces/*.
  const candId = candidat?.candidate_id ?? candidat?.canonical_candidate_id ?? candidat?.id;

  useEffect(() => { loadPieces(); }, [candId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const headers = { 'Authorization': `Bearer ${APIService.getToken()}` };

  // MÊME FICHIER QUE LA RANGÉE CV DE LA FICHE ? L'identité qui fait foi est
  // celle du FICHIER (public_id kDrive, sinon l'URL) — le nom ne la donne
  // qu'en dernier recours : deux copies renommées portent des noms
  // différents, et deux fichiers différents peuvent porter le même nom.
  //
  // Cette reconnaissance ne sert plus à ÉCARTER la pièce canonique (elle est
  // désormais affichée comme n'importe quel document, avec la même barre
  // d'icônes) mais à savoir si le CV de la fiche est DÉJÀ là — auquel cas la
  // rangée de repli bâtie sur `cv_url` ferait double emploi.
  const nomCvFiche = (cvFiche?.nom || '').trim().toLowerCase();
  const memeFichierQueCvFiche = (d) => {
    if (!cvFiche?.url) return false;
    const pid = d.public_id ?? d.publicId ?? d.publicIdKdrive ?? null;
    if (pid && cvFiche.publicId) return String(pid) === String(cvFiche.publicId);
    const url = d.url || d.download_url || d.urlBrute || null;
    if (url) return String(url) === String(cvFiche.url);
    return !!nomCvFiche && (d.original_name || d.file_name || d.name || d.originalName || '').trim().toLowerCase() === nomCvFiche;
  };

  const mapDoc = (d) => ({
    id: d.id,
    label: d.label || d.name || d.original_name || d.file_name || 'Document',
    originalName: d.original_name || d.file_name || d.name || '',
    fileSize: d.size ?? d.file_size ?? d.fileSize ?? null,
    mime_type: d.mime_type || d.mimeType || '',
    version: d.version ?? null,           // « CV TAT préparé » : incrémenté à chaque régénération
    createdAt: d.created_at ?? d.uploaded_at ?? null,
    updatedAt: d.updated_at ?? null,
    updatedByName: d.updated_by_name ?? d.updatedByName ?? null,
    createdByName: d.created_by_name ?? d.createdByName ?? null,
    url: d.url || d.download_url || null,
    // Document de POSITIONNEMENT : id dans l'espace position_documents → actions via
    // /position-documents/:id (pas les endpoints candidate_documents). Le champ kind
    // n'existe que sur ces documents.
    //
    // UN DOCUMENT DE LA FICHE N'EST JAMAIS UN DOCUMENT DE POSITIONNEMENT
    // (vécu 26.10, besoin 303 : « je n'arrive ni à supprimer ni à afficher le
    // CV de Fabrice »). Le CV format entreprise vit dans candidate_documents
    // avec la source « cv_prep » : classé ici en document de positionnement,
    // il était ouvert par /position-documents/5474 (404) et supprimé par la
    // même route — un id d'une AUTRE table, qui aurait pu désigner un autre
    // document. L'origine (`__fiche`, posée au chargement) fait foi.
    positionDoc: !d.__fiche && (d.kind != null || d.source === 'cv_prep' || d.position_document_id != null),
    // L'identité candidate_documents du fichier, quand la pièce la porte —
    // c'est elle que l'éditeur utilise pour retrouver LA préparation qui a
    // produit ce document (sans elle, il ouvre « la plus récente du besoin »,
    // parfois une coquille vide).
    candidateDocId: d.candidate_document_id ?? d.candidateDocumentId ?? d.document_id ?? null,
    // « CV TAT préparé » : jamais de remplacement manuel (le backend renvoie 409) —
    // il se remplace par édition de sections + régénération dans l'écran de préparation.
    generatedPrep: d.kind === 'generated' || d.source === 'cv_prep',
    // Identité du FICHIER telle que le serveur la calcule (?fiche=1) : deux
    // entrées qui la partagent sont le même fichier, quel que soit le libellé.
    // Absente = serveur d'avant le contrat → repli sur l'heuristique de libellé.
    fichierRef: d.fichier_ref ?? d.fichierRef ?? null,
    // LE CV PAR DÉFAUT DU COUPLE, choisi par le serveur (29.10) : le même que
    // la liste du besoin et le dossier AO. L'écran ne le devine plus.
    parDefaut: d.par_defaut === true,
    parDefautRaison: d.par_defaut_raison || null,
  });

  const loadPieces = async () => {
    if (candId == null) { setPieces([]); return; }
    setLoading(true);
    try {
      // Sur la carte besoin : documents du POSITIONNEMENT (couple candidat×besoin) —
      // le « CV TAT préparé » y apparaît aux côtés des autres pièces. Repli sur la
      // liste candidate_documents si l'endpoint couple est indisponible (transition).
      let arr = null;
      // Pièces de la FICHE (CNI, permis, CV…) : le serveur les joint désormais
      // sous la clé `fiche` avec `?fiche=1` — une requête au lieu de deux.
      let fiche = null;
      if (besoinId != null) {
        const res = await fetch(`${BASE}/besoins/${besoinId}/candidates/${candId}/documents?fiche=1`, { headers });
        if (res.ok) {
          const j = await res.json().catch(() => ({}));
          arr = j.documents || j.data?.documents || (Array.isArray(j.data) ? j.data : null) || (Array.isArray(j) ? j : []);
          if (Array.isArray(j.fiche)) fiche = j.fiche;
          setDoublonsMasques(Number(j.doublons_masques ?? j.doublonsMasques ?? 0) || 0);
          setAutresBesoins(Number(j.autres_besoins ?? j.autresBesoins ?? 0) || 0);
        }
      }
      if (fiche == null) {
        // Serveur sans `?fiche=1` (transition) ou pièces hors besoin : on relit
        // la liste candidate_documents comme avant.
        const res = await fetch(`${BASE}/candidates/${candId}/documents`, { headers });
        const j = await res.json().catch(() => ({}));
        fiche = j.documents || j.data?.documents || (Array.isArray(j.data) ? j.data : null) || (Array.isArray(j) ? j : []);
      }
      {
        const cand = fiche;
        if (!Array.isArray(arr)) arr = Array.isArray(cand) ? cand.map(d => ({ ...d, __fiche: true })) : [];
        else if (Array.isArray(cand)) {
          // Fusion : une pièce déposée sur la FICHE (CNI, permis…) doit
          // apparaître ici même si l'endpoint du couple ne la renvoie pas —
          // c'était le bug « j'ai uploadé la CNI mais je ne la vois pas ».
          // Déduplication par id candidate_documents ET par l'identité que
          // portent les documents de positionnement (candidate_document_id).
          const idsCouple = new Set(arr.filter(d => d.kind == null && d.source !== 'cv_prep' && d.position_document_id == null).map(d => d.id));
          const idsPortes = new Set(arr.map(d => d.candidate_document_id ?? d.candidateDocumentId ?? d.document_id).filter(v => v != null));
          // Le serveur donne l'identité du fichier : c'est elle qui fait foi
          // pour reconnaître un même fichier des deux côtés — le front n'a
          // plus à la deviner.
          const refsCouple = new Set(arr.map(d => d.fichier_ref ?? d.fichierRef).filter(v => v != null));
          for (const d of cand) {
            const ref = d.fichier_ref ?? d.fichierRef ?? null;
            if (d.id == null || idsCouple.has(d.id) || idsPortes.has(d.id)) continue;
            if (ref != null && refsCouple.has(ref)) continue;
            arr.push({ ...d, __fiche: true });
          }
        }
      }
      // Plus aucun filtre sur le CV : il s'affiche comme les autres pièces,
      // avec la même barre d'actions. C'est tout l'objet du changement.
      setPieces((Array.isArray(arr) ? arr : []).filter(d => d.id != null).map(mapDoc));
    } catch (e) { setPieces([]); } finally { setLoading(false); }
  };

  const formatFileSize = bytes => {
    if (!bytes) return '';
    const k = 1024, sizes = ['B','KB','MB','GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // « maj {date} par {qui} » si le fichier a été écrasé (version > 1), sinon
  // « ajouté le {date} » (upload initial) — dates du couple candidat×besoin.
  const docDateInfo = (p) => {
    // Date ET heure : deux versions d'un même document le même jour ne se
    // distinguent que par l'heure. Minuit pile = date sans heure, on la tait.
    const fmt = (iso) => {
      if (!iso) return null;
      const d = new Date(iso); if (Number.isNaN(d.getTime())) return null;
      const j = d.toLocaleDateString('fr-FR');
      if (d.getHours() === 0 && d.getMinutes() === 0) return j;
      return `${j} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    };
    if ((p.version ?? 1) > 1 && p.updatedAt) { const dt = fmt(p.updatedAt); return dt ? `maj ${dt}${p.updatedByName ? ` par ${p.updatedByName}` : ''}` : null; }
    const dt = fmt(p.createdAt);
    return dt ? `ajouté le ${dt}${p.createdByName ? ` par ${p.createdByName}` : ''}` : null;
  };

  const uploadPiece = async () => {
    if (!newPiece.file || !newPiece.label.trim()) { showNotification('Remplir le libellé et choisir un fichier', 'error'); return; }
    // UN SEUL CV PAR BESOIN : déposer un CV alors qu'il y en a déjà un
    // (rangée CV de la fiche ou pièce CV du couple) le remplace — on
    // prévient avant le départ du fichier.
    const estDepotDeCv = newPiece.typePiece === 'cv_tat' || /(^|[^a-z])cv([^a-z]|$)/i.test(newPiece.label);
    if (estDepotDeCv && (cvs.length > 0 || cvFiche?.url)) {
      const ok = await confirmErpia({
        title: 'Remplacer le CV du besoin ?',
        message: 'Un CV est déjà présent sur ce besoin. Ce dépôt le remplace — il n’y a qu’un seul CV par besoin.',
        confirmLabel: 'Remplacer le CV', tone: 'danger',
      });
      if (!ok) return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', newPiece.file);
      formData.append('label', newPiece.label.trim());
      if (newPiece.typePiece) formData.append('type_piece', newPiece.typePiece);
      // Un CV format entreprise déposé ICI est celui de CE besoin (29.10 : sans
      // le besoin, il remplaçait le CV générique de la fiche et le CV du besoin
      // restait — le dépôt semblait sans effet).
      if (newPiece.typePiece === 'cv_tat' && besoinId != null) formData.append('besoin_id', String(besoinId));
      const res = await fetch(`${BASE}/candidates/${candId}/documents`, { method: 'POST', headers, body: formData });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success !== false) {
        // Le serveur annonce lui-même l'ajout OU le remplacement.
        showNotification(data.message || `Document "${newPiece.label}" ajouté`, 'success');
        setShowUploadModal(false);
        setNewPiece({ label: '', file: null, typePiece: 'document' });
        await loadPieces();
      } else { showNotification(data.error || `Erreur upload (HTTP ${res.status})`, 'error'); }
    } catch (e) { showNotification('Erreur upload', 'error'); } finally { setUploading(false); }
  };

  // Glisser-déposer : chaque fichier part par la même route que « Ajouter »
  // (label = nom du fichier), un envoi par fichier, bilan par fichier avec le
  // motif du serveur en cas de refus.
  const uploadFichiers = async (fichiers) => {
    const liste = Array.from(fichiers || []).filter(Boolean);
    if (!liste.length || candId == null) return;
    setUploading(true);
    const echecs = [];
    const messages = [];
    for (const file of liste) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('label', (file.name || 'Document').replace(/\.[^.]+$/, '').trim() || 'Document');
        formData.append('type_piece', 'document');
        const res = await fetch(`${BASE}/candidates/${candId}/documents`, { method: 'POST', headers, body: formData });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.success === false) throw new Error(data.error || data.message || `HTTP ${res.status}`);
        if (data.message) messages.push(data.message);
      } catch (e) { echecs.push(`${file.name} (${e.message})`); }
    }
    const ok = liste.length - echecs.length;
    // Les messages du serveur disent ajout ou remplacement — repris tels
    // quels tant que le bilan reste lisible, sinon le compte.
    if (!echecs.length) showNotification(messages.length > 0 && messages.length <= 3 ? messages.join(' · ') : (ok > 1 ? `${ok} documents ajoutés` : 'Document ajouté'), 'success');
    else showNotification(`${ok} ajouté(s), ${echecs.length} en échec : ${echecs.join(', ')}`, 'error');
    await loadPieces();
    setUploading(false);
  };

  const confirmDelete = async () => {
    const piece = deleteTarget;
    if (!piece) return;
    setDeleting(true);
    try {
      const url = piece.positionDoc
        ? `${BASE}/position-documents/${piece.id}`
        : `${BASE}/candidates/${candId}/documents/${piece.id}`;
      const res = await fetch(url, { method: 'DELETE', headers });
      // Le refus du serveur est DIT tel quel — « Erreur suppression » ne
      // disait ni pourquoi, ni quoi faire.
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || j.message || `La suppression a été refusée (${res.status}).`);
      }
      showNotification('Document supprimé', 'success');
      setDeleteTarget(null);
      await loadPieces();
    } catch (e) { showNotification(e.message || 'La suppression a échoué.', 'error'); } finally { setDeleting(false); }
  };

  // Remplacement du fichier d'un document de POSITIONNEMENT (uploadé/matrice).
  // Jamais proposé sur le « CV TAT préparé » ; si le backend refuse (409), son
  // message est affiché tel quel (il oriente vers l'édition + régénération cv-prep).
  const startReplace = (piece) => { setReplaceTarget(piece); replaceFileRef.current?.click(); };
  const doReplace = async (file) => {
    const piece = replaceTarget;
    setReplaceTarget(null);
    if (!piece || !file) return;
    setReplacingId(piece.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BASE}/position-documents/${piece.id}/replace`, { method: 'PUT', headers, body: fd });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409) { showNotification(data.error || data.message || 'Remplacement refusé', 'error'); return; }
      if (!res.ok || data.success === false) throw new Error(data.error || `HTTP ${res.status}`);
      showNotification('Document remplacé', 'success');
      await loadPieces();
    } catch (e) { showNotification('Erreur remplacement : ' + e.message, 'error'); } finally { setReplacingId(null); }
  };

  const confirmRename = async () => {
    const name = (renameTarget?.value || '').trim();
    if (!renameTarget || !name) return;
    if (name === renameTarget.piece.label) { setRenameTarget(null); return; }
    setRenaming(true);
    try {
      const res = await fetch(`${BASE}/candidates/${candId}/documents/${renameTarget.piece.id}`, {
        method: 'PATCH', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: name, name }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showNotification('Document renommé', 'success');
      setRenameTarget(null);
      await loadPieces();
    } catch (e) { showNotification('Erreur renommage : ' + e.message, 'error'); } finally { setRenaming(false); }
  };

  // Contrat auto-signé : produit et signé par le système, il n'a rien à faire
  // dans les pièces de la fiche — il y pousse hors de vue le CV et les
  // documents qui servent tous les jours. Il reste dans le dossier AO, son
  // contexte de production.
  const estContratAutoSigne = (pz) => {
    const type = `${pz.typePiece || pz.type_piece || pz.type || ''} ${pz.source || ''}`.toLowerCase();
    const nom = `${pz.label || ''} ${pz.originalName || pz.original_name || ''}`;
    if (/docsign|signd|auto[_ -]?sign/i.test(type) || /contrat|contract/.test(type)) return true;
    return /(^|[^a-z])(cdi|cdd)[_ -]/i.test(nom) || /contrat\s+(auto[- ])?sign/i.test(nom);
  };

  // Un CV n'est pas un « document complémentaire » : il a sa propre section,
  // le CV format entreprise d'abord — c'est lui qui part chez le client.
  // La reconnaissance est celle de utils/cvDocument : la MÊME qui décide si
  // l'aperçu propose « Éditer ». Deux règles, c'était une pièce rangée dans la
  // section CV mais sans bouton d'édition.
  const estCvPiece = estCvDocument;
  const visibles = pieces.filter(pz => !estContratAutoSigne(pz));
  const cvs = visibles.filter(estCvPiece);
  const autres = visibles.filter(pz => !estCvPiece(pz));

  // Deux pièces dont le libellé ne diffère que par la casse ou les accents
  // (« Matrice de compétences » / « Matrice de Compétences ») : le serveur les
  // a enregistrées comme deux documents au lieu de remplacer. On signale les
  // plus anciennes du groupe — la suppression reste un geste de l'utilisateur,
  // jamais un masquage silencieux (les fichiers peuvent différer).
  // Le serveur publie fichier_ref : deux copies RENOMMÉES du même fichier sont
  // alors reconnues, ce que le libellé seul ne voyait pas.
  // Mais l'inverse est vrai aussi, et c'est le cas vécu le 09.09 : deux
  // « Matrice de compétences » de fichiers DIFFÉRENTS, qui n'étaient plus
  // signalées du tout depuis qu'on ne groupait QUE par fichier. Le
  // rapprochement se fait donc sur les DEUX identités — même fichier, ou même
  // libellé. Le serveur fond désormais les doublons d'une même pièce du
  // couple ; ce qui arrive quand même jusqu'ici (pièces de la fiche, libellés
  // recopiés à la main) reste SIGNALÉ, jamais masqué.
  const normLibelle = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();
  const clesGroupe = (pz) => [pz.fichierRef != null ? `ref:${pz.fichierRef}` : null, normLibelle(pz.label) || null].filter(Boolean);
  const groupesLibelle = new Map();
  for (const pz of visibles) {
    const t = Date.parse(pz.updatedAt || pz.createdAt || '') || 0;
    for (const k of clesGroupe(pz)) {
      const g = groupesLibelle.get(k) || { n: 0, dernier: null, t: -1 };
      g.n += 1;
      if (t >= g.t) { g.t = t; g.dernier = pz.id; }
      groupesLibelle.set(k, g);
    }
  }
  // Signalé dès qu'UNE des deux identités le range derrière un plus récent :
  // exiger les deux laisserait passer le cas où seul le libellé est commun.
  const estDoublonAncien = (pz) => clesGroupe(pz).some(k => {
    const g = groupesLibelle.get(k);
    return !!g && g.n > 1 && g.dernier !== pz.id;
  });
  const sousTitre = 'text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-2';

  // LE CV DE LA FICHE QUE LA LISTE CANONIQUE N'A PAS. Tant que le miroir
  // legacy → candidate_documents n'a pas tout rattrapé, une fiche peut n'avoir
  // son CV que dans `cv_url`. Il devient une rangée comme les autres — pas une
  // barre d'actions à part.
  // Le serveur a désigné le CV du couple : la rangée de repli ferait double
  // emploi (« 1 autre fichier » qui était le même CV).
  const cvDejaDansLaListe = cvFiche?.url ? (pieces.some(memeFichierQueCvFiche) || pieces.some(p => p.parDefaut)) : false;
  const rangeeCvFiche = (cvFiche?.url && !cvDejaDansLaListe) ? {
    id: 'cv-fiche',
    legacyCvFiche: true,                      // aucune route candidate_documents derrière
    estCv: true,                              // c'est LE CV de la fiche — « Karina_2024.pdf » aussi
    label: cvFiche.nom || 'CV du candidat',
    originalName: cvFiche.nom || '',
    fileSize: cvFiche.size ?? null,
    createdAt: cvFiche.ajouteLe ?? null,
    createdByName: cvFiche.ajoutePar ?? null,
    url: cvFiche.url,
    estEntreprise: cvFiche.estEntreprise === true,
    kdrive: cvFiche.kdrive === true,
    version: null, positionDoc: false, generatedPrep: false, candidateDocId: null, fichierRef: null,
  } : null;

  // Ce que le CV dit de lui-même, et que les autres documents n'ont pas à
  // dire : s'il part chez le client (format entreprise) ou s'il s'agit du
  // fichier déposé à l'origine, et d'où il vient.
  // « Format entreprise » = ce CV part chez le client. PAS « ce fichier a été
  // fabriqué par l'ERP » : le NDA signé est fabriqué par l'ERP lui aussi, et
  // il portait cette pastille (vécu 15.09). Le CV produit par la préparation
  // en est un ; pour le reste, c'est le libellé qui le dit.
  const estCvEntreprise = (pz) => pz.estEntreprise === true || estCvProduit(pz)
    || /format[ _]?entreprise|cv[_ ]?tat/i.test(`${pz.typePiece || ''} ${pz.label || ''} ${pz.originalName || ''}`);
  const vientDeKdrive = (pz) => pz.kdrive === true
    || /(?:connect\.drive\.infomaniak|kdrive\.infomaniak|\/dav\/files\/)/.test(pz.url || '');

  // L'aperçu et le téléchargement d'une rangée de repli passent par le proxy
  // de la fiche : c'est le back qui sert le fichier, jamais l'URL de stockage
  // en direct (fetch cross-origin → « Failed to fetch »).
  const proxyCv = `${BASE}/candidats/cv-proxy/${candId}`;

  // UN SEUL CV EN TÊTE (demande 08.09 : « on doit toujours n'avoir qu'un seul
  // CV »). Celui du besoin est le format entreprise s'il existe — c'est lui
  // qui part chez le client — sinon le plus récent. Les autres sont RANGÉS
  // derrière un lien.
  //
  // LE PLUS RÉCENT, PAS LE PREMIER TROUVÉ (vécu 11.09 : « j'ai plusieurs CV
  // sur le besoin, garde que le dernier »). Quand plusieurs CV format
  // entreprise arrivent, `find` prenait celui que le serveur avait listé en
  // premier — c'est-à-dire le plus ANCIEN : on ouvrait une version périmée en
  // croyant ouvrir la dernière, et les régénérations semblaient sans effet.
  const tousLesCv = [...(rangeeCvFiche ? [rangeeCvFiche] : []), ...cvs];
  const dateDe = (pz) => Date.parse(pz.updatedAt || pz.createdAt || '') || 0;
  const plusRecent = (liste) => liste.slice().sort((a, b) => dateDe(b) - dateDe(a))[0] || null;
  // Le serveur dit lequel (par_defaut, utils/cvDuCouple) ; à défaut (serveur
  // d'avant), le plus récent des CV format entreprise.
  const cvPrincipal = tousLesCv.find(pz => pz.parDefaut)
    || plusRecent(tousLesCv.filter(estCvEntreprise))
    || plusRecent(tousLesCv)
    || null;
  const cvSecondaires = tousLesCv.filter(pz => pz !== cvPrincipal);

  const rangeePiece = (piece) => (
            <div key={piece.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <span className="text-base text-gray-400"><Paperclip className="w-4 h-4" /></span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-800 text-sm truncate flex items-center gap-2">
                    <span className="truncate">{piece.label}</span>
                    {piece.version != null && <span className="flex-shrink-0 text-[10.5px] font-semibold bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">v{piece.version}</span>}
                    {/* Réservé au CV : c'est la seule pièce dont il faut savoir
                        si elle part chez le client ou si c'est le dépôt d'origine. */}
                    {estCvPiece(piece) && (estCvEntreprise(piece) ? (
                      <span className="flex-shrink-0 text-[10.5px] font-semibold px-1.5 py-0.5 rounded"
                        style={{ background: 'var(--erp-success-soft)', color: 'var(--erp-success)' }}
                        title="Préparé au format de l'entreprise — celui qui part chez le client">format entreprise</span>
                    ) : (
                      <span className="flex-shrink-0 text-[10.5px] font-semibold bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded"
                        title="Le fichier déposé à l'origine — pas celui présenté au client">original</span>
                    ))}
                    {vientDeKdrive(piece) && <span className="bd-tag-kdrive" title="Importé du Vivier RH">kDrive</span>}
                    {estDoublonAncien(piece) && (
                      <span className="flex-shrink-0 text-[10.5px] font-semibold px-1.5 py-0.5 rounded"
                        style={{ background: 'var(--erp-warning-soft, rgba(138,98,21,0.13))', color: 'var(--erp-warning)' }}
                        title="Un document plus récent porte le même libellé — celui-ci est probablement un doublon à supprimer.">
                        doublon probable
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{[piece.originalName, piece.fileSize ? formatFileSize(piece.fileSize) : null, docDateInfo(piece)].filter(Boolean).join(' · ')}</div>
                </div>
              </div>
              <div className="flex items-center space-x-2 ml-3 flex-shrink-0">
                {/* Aperçu EN MODALE dans les deux cas : viewer canonique pour les
                    candidate_documents ; viewer par URL directe (PDF/DOCX rendus, pas de
                    téléchargement forcé) pour un document de positionnement. */}
                {(!piece.positionDoc || piece.url) && (
                  <button onClick={() => setViewingPiece(piece)} title="Aperçu" className={BTN_ICONE}>
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                )}
                {/* Renommer écrit sur candidate_documents : la rangée de repli
                    n'en a pas, le bouton ne ment pas en s'affichant quand même. */}
                {!piece.positionDoc && !piece.legacyCvFiche && (
                  <button onClick={() => setRenameTarget({ piece, value: piece.label })} title="Renommer" className={BTN_ICONE}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
                {piece.legacyCvFiche
                  ? <a href={avecCle(`${proxyCv}?download=1`)} download title="Télécharger"
                      className={`${BTN_ICONE} inline-flex`}>
                      <Download className="w-3.5 h-3.5" />
                    </a>
                  : piece.id != null && <PieceDownload piece={piece} candId={candId} />}
                {piece.positionDoc && !piece.generatedPrep && (
                  <button onClick={() => startReplace(piece)} disabled={replacingId != null} title="Remplacer le fichier" className={`${BTN_ICONE} disabled:opacity-50`}>
                    <RefreshCw className={`w-3.5 h-3.5 ${replacingId === piece.id ? 'animate-spin' : ''}`} />
                  </button>
                )}
                {/* La rangée de repli n'a pas de ligne candidate_documents à
                    supprimer : c'est le CV de la fiche, la carte sait le faire. */}
                <button onClick={() => (piece.legacyCvFiche ? onSupprimerCvFiche?.() : setDeleteTarget(piece))}
                  disabled={piece.legacyCvFiche && suppressionCvEnCours}
                  title="Supprimer" className={`${BTN_ICONE_DANGER} disabled:opacity-50`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );

  return (
    <div className={`border rounded-xl p-4 mt-4 bg-white relative ${dropDocs ? 'border-red-400 border-dashed bg-red-50' : 'border-gray-200'}`}
      onDragOver={e => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDropDocs(true); } }}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDropDocs(false); }}
      onDrop={e => { e.preventDefault(); setDropDocs(false); const fs = Array.from(e.dataTransfer?.files || []); if (fs.length) uploadFichiers(fs); }}>
      {dropDocs && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl pointer-events-none" style={{ background: 'rgba(255,255,255,0.72)' }}>
          <span className="text-sm font-bold" style={{ color: 'var(--brand, #E2231A)' }}>Déposez les fichiers ici — ils rejoignent les documents du candidat</span>
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
          Documents
          <span className="bg-gray-100 text-gray-600 text-xs font-bold px-2 py-0.5 rounded-md">{pieces.length + (rangeeCvFiche ? 1 : 0)}</span>
          {doublonsMasques > 0 && (
            <span title="Le même fichier déposé plusieurs fois n'apparaît qu'une fois ici"
              className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded"
              style={{ background: 'var(--erp-surface-muted)', color: 'var(--erp-text-tertiary)' }}>
              {doublonsMasques} doublon{doublonsMasques > 1 ? 's' : ''} regroupé{doublonsMasques > 1 ? 's' : ''}
            </span>
          )}
          {autresBesoins > 0 && (
            <span title="Ce candidat a des documents préparés pour d'autres besoins (leur CV format entreprise, par exemple). Ils restent sur ces besoins-là — cette liste ne montre que les documents de celui-ci."
              className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded"
              style={{ background: 'var(--erp-surface-muted)', color: 'var(--erp-text-tertiary)' }}>
              {autresBesoins} sur d'autres besoins
            </span>
          )}
        </h3>
        <button onClick={() => setShowUploadModal(true)} className="bg-gray-900 hover:bg-black text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors">
          <Plus className="w-3.5 h-3.5" /> Ajouter
        </button>
      </div>

      {loading ? <div className="text-center py-4 text-gray-400 text-sm">Chargement...</div>
      : (<>
        {/* Les deux sections s'affichent TOUJOURS, chacune avec son état vide.
            « Aucun CV » est une information — et la seule place d'où en
            déposer un ; la cacher derrière un état vide global faisait
            disparaître le geste. */}
        <div className="mb-4">
            <div className={sousTitre}>CV</div>
            {/* POURQUOI ce CV, dit par le serveur : c'est le même que le
                dossier AO enverra (29.10). */}
            {cvPrincipal?.parDefautRaison && (
              <div className="text-[11px] mb-1.5" style={{ color: 'var(--erp-text-tertiary)' }}>
                {cvPrincipal.parDefautRaison} — le même dans le dossier AO
              </div>
            )}
            <div className="space-y-2">
              {tousLesCv.length === 0 && (
                <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-dashed border-gray-200">
                  <span className="text-xs text-gray-400 italic">Aucun CV sur cette fiche</span>
                  {onUploadCvFiche && (
                    <label className={`${BTN_ICONE} px-2.5 inline-flex items-center gap-1.5 text-xs font-semibold cursor-pointer`}>
                      <Upload className="w-3.5 h-3.5" /> Déposer un CV
                      <input type="file" accept=".pdf,.doc,.docx" className="hidden"
                        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onUploadCvFiche(f); }} />
                    </label>
                  )}
                </div>
              )}
              {/* LE CV DU BESOIN D'ABORD, les autres fichiers CV rangés
                  derrière un lien — jamais effacés : un fichier qui disparaît
                  sans un mot est pire qu'un doublon. Celui « du besoin » est
                  le format entreprise s'il existe (c'est lui qui part chez le
                  client), sinon le plus récent. */}
              {cvPrincipal && rangeePiece(cvPrincipal)}
              {cvSecondaires.length > 0 && (<>
                <button type="button" onClick={() => setVoirAutresCv(v => !v)} className="pj-autres-cv">
                  {voirAutresCv
                    ? 'Masquer les autres fichiers CV'
                    : `${cvSecondaires.length} autre${cvSecondaires.length > 1 ? 's' : ''} fichier${cvSecondaires.length > 1 ? 's' : ''} CV sur cette fiche — afficher`}
                </button>
                {voirAutresCv && cvSecondaires.map(rangeePiece)}
              </>)}
            </div>
        </div>
        <div>
          <div className={sousTitre}>Complémentaires</div>
          {autres.length === 0
            ? <p className="text-xs text-gray-400">Aucun autre document — ex : matrice de compétences, références…</p>
            : <div className="space-y-2">{autres.map(rangeePiece)}</div>}
        </div>
      </>)}

      {/* Input caché pour le remplacement d'un document de positionnement */}
      <input ref={replaceFileRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls" className="hidden"
        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; doReplace(f); }} />

      {/* Visualiseur canonique dans les DEUX cas — toujours servi par le back (docx→pdf),
          jamais l'URL kDrive brute (fetch cross-origin → « Failed to fetch »). Doc de
          positionnement → endpoint /position-documents/:id/preview ; sinon candidate_documents.
          À la fermeture, la liste se recharge — puis se recharge ENCORE quelques
          secondes plus tard : l'éditeur (ouvert depuis le viewer) régénère le
          document au moment de sa fermeture, et la génération côté serveur peut
          atterrir APRÈS la première relecture. Sans le second passage, voir le
          nouveau document dépendait d'une course de vitesse. */}
      {/* « Éditer » s'ouvre sur TOUT CV — déposé comme produit par le
          générateur. Le bouton était réservé au « CV TAT préparé », au motif
          qu'un CV déposé n'a aucune préparation derrière lui ; c'est faux
          depuis que le lanceur en crée une par reprise fidèle du fichier. Un
          candidat sans CV format entreprise n'avait donc jamais de bouton
          (vécu : « sur Karina je ne peux pas éditer, sur un autre si »). La
          règle — est-ce un CV ? — est celle de utils/cvDocument, la même que
          celle qui range la pièce dans la section CV : une matrice de
          compétences reste hors éditeur, elle se refait par « Matrice ». */}
      {viewingPiece && (viewingPiece.legacyCvFiche
        ? <CanonicalDocumentViewer previewBase={proxyCv} candidateId={candId} cv={estCvDocument(viewingPiece)}
            label={viewingPiece.label} besoinId={besoinId}
            candidatNom={[candidat?.prenom, candidat?.nom].filter(Boolean).join(' ')} showNotification={showNotification}
            onClose={() => { setViewingPiece(null); loadPieces(); }} />
        : viewingPiece.positionDoc
        ? <CanonicalDocumentViewer previewBase={`${BASE}/position-documents/${viewingPiece.id}/preview`}
            docId={viewingPiece.candidateDocId ?? undefined} cv={estCvDocument(viewingPiece)}
            label={viewingPiece.label || viewingPiece.originalName} candidateId={candId} besoinId={besoinId}
            candidatNom={[candidat?.prenom, candidat?.nom].filter(Boolean).join(' ')} showNotification={showNotification}
            onClose={() => { setViewingPiece(null); loadPieces(); setTimeout(loadPieces, 4000); }} />
        : <CanonicalDocumentViewer candidateId={candId} docId={viewingPiece.id} cv={estCvDocument(viewingPiece)}
            label={viewingPiece.label || viewingPiece.originalName} besoinId={besoinId}
            candidatNom={[candidat?.prenom, candidat?.nom].filter(Boolean).join(' ')} showNotification={showNotification}
            onClose={() => { setViewingPiece(null); loadPieces(); setTimeout(loadPieces, 4000); }} />
      )}

      {/* Renommage — modale (remplace le window.prompt natif) */}
      {renameTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4" onClick={() => !renaming && setRenameTarget(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Pencil className="w-4 h-4 text-gray-700" />
                </div>
                <h3 className="font-semibold text-gray-900">Renommer le document</h3>
              </div>
              <button onClick={() => setRenameTarget(null)} disabled={renaming} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-5">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Nouveau nom</label>
              <input type="text" value={renameTarget.value} autoFocus
                onChange={e => setRenameTarget(t => ({ ...t, value: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') confirmRename(); if (e.key === 'Escape' && !renaming) setRenameTarget(null); }}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-gray-400 outline-none" />
              {renameTarget.piece.originalName && <p className="text-[11px] text-gray-400 mt-1.5 truncate">Fichier : {renameTarget.piece.originalName}</p>}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button onClick={() => setRenameTarget(null)} disabled={renaming}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium">Annuler</button>
              <button onClick={confirmRename} disabled={renaming || !(renameTarget.value || '').trim()}
                className="px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl hover:bg-black disabled:opacity-40 flex items-center gap-2">
                {renaming ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />Renommage...</> : <><Pencil className="w-4 h-4" />Renommer</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suppression — confirmation (remplace le window.confirm natif) */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-4 h-4 text-red-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900">Supprimer ce document ?</h3>
                  <p className="text-sm text-gray-500 mt-1 truncate">« {deleteTarget.label} »</p>
                  <p className="text-xs text-gray-400 mt-1">Cette action est irréversible.</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button onClick={() => setDeleteTarget(null)} disabled={deleting}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium">Annuler</button>
              <button onClick={confirmDelete} disabled={deleting}
                className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 disabled:opacity-40 flex items-center gap-2">
                {deleting ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />Suppression...</> : <><Trash2 className="w-4 h-4" />Supprimer</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 text-gray-700" />
                </div>
                <h3 className="font-semibold text-gray-900">Ajouter un document</h3>
              </div>
              <button onClick={() => { setShowUploadModal(false); setNewPiece({ label: '', file: null, typePiece: 'document' }); }} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Libellé *</label>
                <input type="text" value={newPiece.label} onChange={e => setNewPiece({...newPiece, label: e.target.value})}
                  placeholder="Ex: Matrice de compétences..." autoFocus
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-gray-400 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Type de pièce</label>
                <ErpiaSelect value={newPiece.typePiece} onChange={e => setNewPiece({ ...newPiece, typePiece: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-gray-400 outline-none bg-white">
                  {TYPE_PIECE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </ErpiaSelect>
                <p className="text-[11px] text-gray-400 mt-1">Le dossier AO classe la pièce d\u2019après ce type.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Fichier * (max 10MB)</label>
                <label className={`flex flex-col items-center justify-center w-full border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${newPiece.file ? 'border-green-300 bg-green-50' : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'}`}>
                  {newPiece.file ? (
                    <div className="text-center">
                      <div className="text-2xl mb-1 text-gray-400"><i className="bi bi-file-earmark-text" /></div>
                      <div className="text-sm font-medium text-green-700 truncate max-w-xs">{newPiece.file.name}</div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <div className="text-sm text-gray-500">Cliquez pour choisir</div>
                      <div className="text-xs text-gray-400 mt-1">PDF, Word, Excel, Image</div>
                    </div>
                  )}
                  <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
                    onChange={e => { const f = e.target.files[0]; if (f && f.size <= 10*1024*1024) setNewPiece({...newPiece, file: f}); else if (f) showNotification('Fichier trop volumineux (max 10MB)', 'error'); }}
                    className="hidden" />
                </label>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button onClick={() => { setShowUploadModal(false); setNewPiece({ label: '', file: null, typePiece: 'document' }); }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium" disabled={uploading}>Annuler</button>
              <button onClick={uploadPiece} disabled={!newPiece.file || !newPiece.label.trim() || uploading}
                className="px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl hover:bg-black disabled:opacity-40 flex items-center gap-2">
                {uploading ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />Upload...</> : <><Upload className="w-4 h-4" />Ajouter</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PiecesJointesManager;
